import type { GridColDef } from '@mui/x-data-grid'
import { formatNumber } from '@/utils/money'
import { titleCaseName } from '@/utils/text'

export type SaleReferencePageKey =
  | 'ValueUpdates'
  | 'CertificationUpdates'
  | 'Claims'
  | 'Packages'
  | 'ListandValo'
  | 'DeliveryTerms'
  | 'FuturesAllocation'
  | 'CPList'
  | 'Consignee'
  | 'Certification'
  | 'Ex_Calendar'
  | 'DeliveryPlace'
  | 'FuturesLotAdjust'

export type SaleReferenceValueKind = 'text' | 'number' | 'date' | 'datetime'

export interface SaleReferenceColumnConfig {
  field: string
  headerName: string
  kind?: SaleReferenceValueKind
  precision?: number
  width?: number
  minWidth?: number
  flex?: number
  align?: 'left' | 'center' | 'right'
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
  /** Wrap cell text (auto row height) thay vì ellipsis 1 dòng — bật cho cột chữ dài
      (tên KH/NCC, address, note, description...) kể cả khi user chưa resize cột. */
  wrap?: boolean
}

export interface SaleReferencePageConfig {
  pageKey: SaleReferencePageKey
  label: string
  sourceForm: string
  sourceQuery: string
  limit?: number
  columns: SaleReferenceColumnConfig[]
}

export const SALE_REFERENCE_PAGE_ORDER: SaleReferencePageKey[] = [
  'ValueUpdates',
  'CertificationUpdates',
  'Claims',
  'Packages',
  'ListandValo',
  'DeliveryTerms',
  'FuturesAllocation',
  'CPList',
  'Consignee',
  'Certification',
  'Ex_Calendar',
  'DeliveryPlace',
  'FuturesLotAdjust',
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatAccessDate(value: unknown): string {
  if (value == null || value === '') return ''
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = `${value.getDate()}`.padStart(2, '0')
    const month = ACCESS_MONTHS[value.getMonth()]
    const year = `${value.getFullYear()}`
    return `${day}-${month}-${year}`
  }
  const raw = String(value).trim()
  if (!raw) return ''
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  const day = `${parsed.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[parsed.getMonth()]
  const year = `${parsed.getFullYear()}`
  return `${day}-${month}-${year}`
}

export function formatAccessDateTime(value: unknown): string {
  if (value == null || value === '') return ''
  const d = value instanceof Date ? value : new Date(String(value).trim())
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

export function formatSaleReferenceValue(
  value: unknown,
  kind: SaleReferenceValueKind = 'text',
  precision = 0,
): string {
  if (value == null || value === '') return ''
  if (kind === 'date') return formatAccessDate(value)
  if (kind === 'datetime') return formatAccessDateTime(value)
  if (kind === 'number') {
    if (typeof value === 'number') {
      return formatNumber(value, precision)
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? String(value) : formatNumber(parsed, precision)
  }
  return String(value)
}

export function buildSaleReferenceColumns(columns: SaleReferenceColumnConfig[]): GridColDef<Record<string, unknown>>[] {
  return columns.map((column) => {
    const align = column.align ?? (column.kind === 'number' ? 'right' : 'left')
    return {
      field: column.field,
      headerName: column.headerName,
      width: column.width,
      minWidth: column.minWidth,
      flex: column.flex,
      sortable: true,
      filterable: false,
      align,
      headerAlign: align,
      valueFormatter: (value: unknown) => {
        if (column.proper) return titleCaseName(value)
        return column.kind === 'number'
          ? formatSaleReferenceValue(value, 'number', column.precision ?? 0)
          : formatSaleReferenceValue(value, column.kind)
      },
    }
  })
}

export function filterSaleReferenceRows(
  rows: Record<string, unknown>[],
  search: string,
  columns: SaleReferenceColumnConfig[],
): Record<string, unknown>[] {
  const term = search.trim().toLowerCase()
  if (!term) return rows
  return rows.filter((row) =>
    columns.some((column) =>
      (column.proper
        ? titleCaseName(row[column.field])
        : formatSaleReferenceValue(row[column.field], column.kind, column.precision ?? 0)
      )
        .toLowerCase()
        .includes(term),
    ),
  )
}

export const SALE_REFERENCE_PAGE_CONFIGS: Record<SaleReferencePageKey, SaleReferencePageConfig> = {
  ValueUpdates: {
    pageKey: 'ValueUpdates',
    label: 'ValueUpdates',
    sourceForm: 'frmContractSalesQryRefUpdates',
    sourceQuery: 'ContractSalesQryRefUpdates',
    limit: 1000000,
    columns: [
      { field: 'date', headerName: 'Date', kind: 'date', width: 120 },
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'description', headerName: 'Description', minWidth: 260, flex: 1, wrap: true },
      {
        field: 'premium_vs_g2_5percent',
        headerName: 'Premium vs G2 5Percent',
        kind: 'number',
        precision: 4,
        width: 180,
      },
      { field: 'reason_of_change', headerName: 'Reason of Change', minWidth: 260, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated_Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  CertificationUpdates: {
    pageKey: 'CertificationUpdates',
    label: 'CertificationUpdates',
    sourceForm: 'frmContractSalesQryRefCertificationChange',
    sourceQuery: 'ContractSalesQryRefCertifitcationChange',
    limit: 1000000,
    columns: [
      { field: 'changed_date', headerName: 'Changed Date', kind: 'date', width: 140 },
      { field: 'premium_value', headerName: 'Premium Value', kind: 'number', precision: 4, width: 160 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Claims: {
    pageKey: 'Claims',
    label: 'Claims',
    sourceForm: 'frmContractSalesQryRefClaimCondition',
    sourceQuery: 'ContractSalesQryRefClaimCondition',
    limit: 1000000,
    columns: [
      { field: 'organization', headerName: 'Organization', minWidth: 200, flex: 1, wrap: true },
      { field: 'weighing_date', headerName: 'Weighing Date', kind: 'number', precision: 0, width: 140 },
      { field: 'weight_claim', headerName: 'Weight Claim', kind: 'number', precision: 0, width: 140 },
      { field: 'quality_claims', headerName: 'Quality Claims', kind: 'number', precision: 0, width: 150 },
      { field: 'others', headerName: 'Others', kind: 'number', precision: 0, width: 120 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Packages: {
    pageKey: 'Packages',
    label: 'Packages',
    sourceForm: 'frmContractSalesQryRefPackage',
    sourceQuery: 'ContractSalesQryRefPackage',
    limit: 1000000,
    columns: [
      { field: 'packaging_code', headerName: 'Packaging Code', width: 160 },
      { field: 'packaging', headerName: 'Packaging', minWidth: 200, flex: 1, wrap: true },
      { field: 'prem_discount', headerName: 'Prem Discount', kind: 'number', precision: 0, width: 140 },
      { field: 'nw_per_bag_kg', headerName: 'Nw Per Bag Kg', kind: 'number', precision: 0, width: 140 },
      { field: 'description_eng', headerName: 'Description Eng', minWidth: 240, flex: 1, wrap: true },
      { field: 'description_vn', headerName: 'Description Vn', minWidth: 240, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  ListandValo: {
    pageKey: 'ListandValo',
    label: 'ListandValo',
    sourceForm: 'frmContractSalesQryRefListValo',
    sourceQuery: 'ContractSalesQryRefListValo',
    limit: 1000000,
    columns: [
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'description', headerName: 'Description', minWidth: 220, flex: 1, wrap: true },
      { field: 'bean_size', headerName: 'Bean Size', width: 120 },
      { field: 'phase', headerName: 'Phase', width: 120 },
      { field: 'category', headerName: 'Category', width: 120 },
      { field: 'ranked', headerName: 'Rranked', kind: 'number', precision: 0, width: 100 },
      {
        field: 'premium_vs_g2',
        headerName: 'Premium vs G2 (New)',
        kind: 'number',
        precision: 4,
        width: 180,
      },
      {
        field: 'valuation_in_use',
        headerName: 'Valuation in Use (New)',
        kind: 'number',
        precision: 4,
        width: 190,
      },
      { field: 'special_requirements', headerName: 'Special Requirements', minWidth: 240, flex: 1, wrap: true },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'description_on_contract', headerName: 'Description on Contract', minWidth: 240, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  DeliveryTerms: {
    pageKey: 'DeliveryTerms',
    label: 'DeliveryTerms',
    sourceForm: 'frmContractSalesQryRefDeliveryTerm',
    sourceQuery: 'ContractSalesQryRefDeliveryTerm',
    limit: 1000000,
    columns: [
      { field: 'delivery_term', headerName: 'Delivery Term', width: 160 },
      { field: 'description', headerName: 'Description', minWidth: 220, flex: 1, wrap: true },
      { field: 'description_on_contract', headerName: 'Description on Contract', minWidth: 240, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  FuturesAllocation: {
    pageKey: 'FuturesAllocation',
    label: 'FuturesAllocation',
    sourceForm: 'frmContractSalesQryRefFuturesAllocation',
    sourceQuery: 'ContractSalesQryRefFuturesAllocation',
    limit: 1000000,
    columns: [
      { field: 'date', headerName: 'Date', kind: 'date', width: 120 },
      { field: 'contract_id', headerName: 'Contract ID', width: 140 },
      { field: 'ps', headerName: 'P/S', minWidth: 120, flex: 1 },
      { field: 'cp', headerName: 'CP', minWidth: 120, flex: 1, proper: true },
      { field: 'kg', headerName: 'Kg', kind: 'number', precision: 0, width: 120 },
      { field: 'type', headerName: 'Type', width: 120 },
      { field: 'lot_allocating', headerName: 'Lot Allocating', kind: 'number', precision: 0, width: 140 },
      { field: 'futures_id', headerName: 'Futures ID', width: 140 },
      { field: 'buysell', headerName: 'Buy/Sell', width: 120 },
      { field: 'month', headerName: 'Month', width: 120 },
      { field: 'price', headerName: 'Price', kind: 'number', precision: 4, width: 120 },
      { field: 'balance', headerName: 'Balance', kind: 'number', precision: 4, width: 140 },
    ],
  },
  CPList: {
    pageKey: 'CPList',
    label: 'CPList',
    sourceForm: 'frmContractSalesQryRefCPList',
    sourceQuery: 'ContractSalesQryRefCPList',
    limit: 1000000,
    columns: [
      { field: 'code', headerName: 'Code', width: 140 },
      { field: 'name_cp', headerName: 'Name', minWidth: 220, flex: 1, proper: true, wrap: true },
      { field: 'short_name', headerName: 'Short Name', minWidth: 160, flex: 1, proper: true, wrap: true },
      { field: 'address', headerName: 'Address', minWidth: 220, flex: 1, wrap: true },
      { field: 'phone', headerName: 'Phone', width: 140 },
      { field: 'mobile', headerName: 'Mobile', width: 140 },
      { field: 'fax', headerName: 'Fax', width: 140 },
      { field: 'vat_code', headerName: 'VAT Code', width: 140 },
      { field: 'email', headerName: 'Email', minWidth: 180, flex: 1, wrap: true },
      { field: 'status', headerName: 'Status', width: 120 },
      { field: 'rep', headerName: 'Rep', minWidth: 160, flex: 1, proper: true, wrap: true },
      { field: 'position', headerName: 'Position', minWidth: 140, flex: 1, wrap: true },
      { field: 'reg_no', headerName: 'Reg No', width: 140 },
      { field: 'issued_by', headerName: 'Issued By', minWidth: 180, flex: 1, wrap: true },
      { field: 'issuing_date', headerName: 'Issuing Date', kind: 'date', width: 140 },
      { field: 'id_no', headerName: 'ID No', width: 140 },
      { field: 'id_place', headerName: 'ID Place', minWidth: 160, flex: 1, wrap: true },
      { field: 'id_date', headerName: 'ID Date', kind: 'date', width: 140 },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'sale_offshore_onshore', headerName: 'Sale Offshore Onshore', minWidth: 220, flex: 1, wrap: true },
      { field: 'type_cp', headerName: 'Type', width: 120 },
      { field: 'status_cp', headerName: 'Using', width: 120 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Consignee: {
    pageKey: 'Consignee',
    label: 'Consignee',
    sourceForm: 'frmContractSalesQryRefConsignee',
    sourceQuery: 'ContractSalesQryRefConsignee',
    limit: 1000000,
    columns: [
      { field: 'consignee_code', headerName: 'Code', width: 140 },
      { field: 'consignee_name', headerName: 'Name', minWidth: 220, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  Certification: {
    pageKey: 'Certification',
    label: 'Certification',
    sourceForm: 'frmContractSalesQryRefCertification',
    sourceQuery: 'MdQryCertification',
    limit: 1000000,
    columns: [
      { field: 'certification', headerName: 'Certification', width: 160 },
      { field: 'premium', headerName: 'Premium (New)', kind: 'number', precision: 4, width: 160 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
      { field: 'max_date_change', headerName: 'Date Change (New)', kind: 'date', width: 160 },
    ],
  },
  Ex_Calendar: {
    pageKey: 'Ex_Calendar',
    label: 'Ex_Calendar',
    sourceForm: 'frmContractSalesQryRefCalendar',
    sourceQuery: 'ContractSalesQryRefCalendar',
    limit: 1000000,
    columns: [
      // Year hiển thị integer thuần (không grouping "2,026") nên không gắn kind 'number'.
      { field: 'year', headerName: 'Year', align: 'right', width: 100 },
      { field: 'month', headerName: 'Month', kind: 'number', precision: 0, width: 100 },
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'first_business_day', headerName: 'First Business Day', kind: 'date', width: 160 },
      { field: 'last_business_day', headerName: 'Last Business Day', kind: 'date', width: 160 },
      { field: 'first_notice_day', headerName: 'First Notice Day', kind: 'date', width: 160 },
      { field: 'last_trading_day', headerName: 'Last Trading Day', kind: 'date', width: 160 },
      { field: 'last_fixing_day', headerName: 'Last Fixing Day', kind: 'date', width: 160 },
      { field: 'ex', headerName: 'Exchange', width: 120 },
      { field: 'date_updated', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_updated', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
      { field: 'ex_text', headerName: 'Exchange Text', minWidth: 180, flex: 1, wrap: true },
      { field: 'full_symbol', headerName: 'Full symbol', minWidth: 160, flex: 1, wrap: true },
    ],
  },
  DeliveryPlace: {
    pageKey: 'DeliveryPlace',
    label: 'DeliveryPlace',
    sourceForm: 'frmContractSalesQryRefDeliveryPlace',
    sourceQuery: 'ContractSalesQryRefDeliveryPlace',
    limit: 1000000,
    columns: [
      { field: 'delivery_place_code', headerName: 'Delivery Place Code', width: 180 },
      { field: 'delivery_place', headerName: 'Delivery Place', minWidth: 220, flex: 1, wrap: true },
      {
        field: 'logistic_costs_to_fob',
        headerName: 'Logistic Costs to_FOB',
        kind: 'number',
        precision: 2,
        width: 180,
      },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
      { field: 'address', headerName: 'Address', minWidth: 220, flex: 1, wrap: true },
    ],
  },
  FuturesLotAdjust: {
    pageKey: 'FuturesLotAdjust',
    label: 'FuturesLotAdjust',
    sourceForm: 'frmContractSalesQryRefAdjustLot',
    sourceQuery: 'ContractSalesQryRefAdjustLot',
    limit: 1000000,
    columns: [
      { field: 'contract_no', headerName: 'Contract No', width: 160 },
      { field: 'adjusted_lot', headerName: 'Adjusted Lot', kind: 'number', precision: 0, width: 140 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 160, flex: 1, wrap: true },
    ],
  },
}

export function getSaleReferencePageConfig(pageKey: SaleReferencePageKey): SaleReferencePageConfig {
  return SALE_REFERENCE_PAGE_CONFIGS[pageKey]
}
